import loginScreenImageUrl from "@/assets/login-screen.svg"
import { Button } from "@/components/chadcn/Button"
import { Label } from "@/components/chadcn/Label"
import HeroImage from "@/components/HeroImage"
import { PhoneInput } from "@/components/InputFields"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useSelfRegisterDeviceMutation } from "@/data/queries"
import { useAuthTokensAtom, useDeviceCodeInfo } from "@/data/store"
import { ROUTES } from "@/routes"
import { selfRegisterDeviceSchema, type SelfRegisterDeviceFormData } from "@/schemas/devices"
import { zodResolver } from "@hookform/resolvers/zod"
import { Instant } from "@js-joda/core"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { generatePath, useNavigate, useParams } from "react-router"

const DEVICE_CODE_TTL_MS = 30 * 24 * 60 * 60 * 1000

const RegisterDeviceLandingPage = () => {
  const { deviceCode } = useParams<{ deviceCode: string }>()
  const navigate = useNavigate()
  const [authTokens] = useAuthTokensAtom()
  const [, setDeviceCodeInfo] = useDeviceCodeInfo()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const { mutateAsync: selfRegisterDevice, isPending } = useSelfRegisterDeviceMutation()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SelfRegisterDeviceFormData>({
    mode: "all",
    defaultValues: { deviceCode: deviceCode ?? "", phoneNumber: "" },
    resolver: zodResolver(selfRegisterDeviceSchema),
  })

  useEffect(() => {
    if (authTokens && deviceCode) {
      navigate(generatePath(ROUTES.ADD_DEVICE, { deviceCode }), { replace: true })
    }
  }, [authTokens, deviceCode, navigate])

  const onSubmit = async (formData: SelfRegisterDeviceFormData) => {
    setSubmitError(null)
    try {
      await selfRegisterDevice(formData)
      if (deviceCode) {
        setDeviceCodeInfo({
          deviceCode,
          expiresAt: Instant.now().plusMillis(DEVICE_CODE_TTL_MS).toString(),
        })
      }
      navigate(ROUTES.CHECK_SMS, {
        state: { deviceCode, phoneNumber: formData.phoneNumber },
      })
    } catch {
      setSubmitError(
        "Failed to register blood glucose monitor. Please try again or contact Bloomlife.",
      )
    }
  }

  return (
    <Page showAppbar={false}>
      <HeroImage src={loginScreenImageUrl} />
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-h1">Welcome to Device Registration</h1>
          <h2 className="text-h2 text-primary-contrast/70">
            Let&apos;s get your blood glucose monitor set up and connected to your account
          </h2>
        </div>

        {submitError && (
          <div
            role="alert"
            className="rounded-md border border-error/40 bg-error/10 p-3 text-sm text-error"
          >
            {submitError}
          </div>
        )}

        <form className="flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-1">
            <Label htmlFor="phoneNumber">Phone number</Label>
            <PhoneInput
              id="phoneNumber"
              disabled={isPending}
              {...register("phoneNumber")}
              placeholder="+13105551234"
              isError={!!errors.phoneNumber?.message}
            />
            {errors.phoneNumber?.message && (
              <p className="text-sm text-error">{errors.phoneNumber.message}</p>
            )}
          </div>

          <Button type="submit" disabled={isPending} className="flex w-full gap-3 py-2 text-base">
            {isPending && <Spinner />}
            {isPending ? "Verifying..." : "Continue"}
          </Button>
        </form>
      </div>
    </Page>
  )
}

export default RegisterDeviceLandingPage
